import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";

import { i18n } from "../../translate/i18n";
import { Button, CircularProgress, Grid, TextField, Typography, Box, Card, CardContent } from "@material-ui/core";
import { Field, Form, Formik } from "formik";
import toastError from "../../errors/toastError";
import { toast } from "sonner";
import api from "../../services/api";
import axios from "axios";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import Title from '../../components/Title';

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2, 3),
    borderRadius: 14,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid #E2E8F0",
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  sectionPaper: {
    padding: theme.spacing(2, 3),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid #E2E8F0",
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  sectionTitle: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 18,
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
    marginBottom: theme.spacing(1.5),
  },
  sectionSubtitle: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 16,
    fontWeight: 600,
    color: "#35af9a",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  sectionText: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 14,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    lineHeight: 1.6,
    "& p": {
      marginBottom: theme.spacing(1),
    },
    "& ul, & ol": {
      paddingLeft: theme.spacing(3),
      marginBottom: theme.spacing(1),
    },
    "& li": {
      marginBottom: theme.spacing(0.5),
    },
    "& b": {
      color: theme.palette.type === "dark" ? "#E2E8F0" : "#334155",
      fontWeight: 600,
    },
  },
  formContainer: {
    width: "100%",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontFamily: '"Inter", "Poppins", sans-serif',
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#35af9a",
      },
    },
    "& .MuiInputLabel-outlined": {
      fontFamily: '"Inter", "Poppins", sans-serif',
      "&.Mui-focused": {
        color: "#35af9a",
      },
    },
  },
  fileInput: {
    width: "100%",
    padding: theme.spacing(1.5),
    borderRadius: 10,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid #E2E8F0",
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 14,
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255,255,255,0.02)"
      : "#F8FAFC",
    "&:hover": {
      borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
    },
    "&:focus": {
      borderColor: "#35af9a",
      outline: "none",
    },
  },
  submitButton: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    borderRadius: 10,
    padding: "8px 24px",
    backgroundColor: "#35af9a",
    color: "#FFFFFF",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#2d9a87",
      boxShadow: "0 2px 8px rgba(53,175,154,0.3)",
    },
  },
  buttonProgress: {
    color: "#35af9a",
  },
  textRight: {
    textAlign: "right",
    marginTop: theme.spacing(1),
  },
}));

const MessagesAPI = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const [formMessageTextData,] = useState({ token: '', number: '', body: '', userId: '', queueId: '' })
  const [formMessageMediaData,] = useState({ token: '', number: '', medias: '', body:'', userId: '', queueId: '' })
  const [file, setFile] = useState({})
  const { user } = useContext(AuthContext);

  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useExternalApi) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEndpoint = () => {
    return process.env.REACT_APP_BACKEND_URL + '/api/messages/send'
  }

  const handleSendTextMessage = async (values) => {
    const { number, body, userId, queueId } = values;
    const data = { number, body, userId, queueId };
    try {
      await axios.request({
        url: getEndpoint(),
        method: 'POST',
        data,
        headers: {
          'Content-type': 'application/json',
          'Authorization': `Bearer ${values.token}` 
        }
      })
      toast.success('Mensagem enviada com sucesso');
    } catch (err) {
      toastError(err);
    }
  }

  const handleSendMediaMessage = async (values) => {
    try {
      const firstFile = file[0];
      const data = new FormData();
      data.append('number', values.number);
      data.append('body', values.body ? values.body: firstFile.name);
      data.append('userId', values.userId);
      data.append('queueId', values.queueId);
      data.append('medias', firstFile);
      await axios.request({
        url: getEndpoint(),
        method: 'POST',
        data,
        headers: {
          'Content-type': 'multipart/form-data',
          'Authorization': `Bearer ${values.token}`
        }
      })
      toast.success('Mensagem enviada com sucesso');
    } catch (err) {
      toastError(err);
    }
  }

  const renderFormMessageText = () => {
    return (
      <Formik
        initialValues={formMessageTextData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendTextMessage(values);
            actions.setSubmitting(false);
            actions.resetForm()
          }, 400);
        }}
        className={classes.elementMargin}
      >
        {({ isSubmitting }) => (
          <Form className={classes.formContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.token")}
                  name="token"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.number")}
                  name="number"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.body")}
                  name="body"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}  md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.userId")}
                  name="userId"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12}  md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.queueId")}
                  name="queueId"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12} className={classes.textRight}>
                <Button
                  type="submit"
                  variant="contained"
                  className={classes.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  ) : 'Enviar'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    )
  }

  const renderFormMessageMedia = () => {
    return (
      <Formik
        initialValues={formMessageMediaData}
        enableReinitialize={true}
        onSubmit={(values, actions) => {
          setTimeout(async () => {
            await handleSendMediaMessage(values);
            actions.setSubmitting(false);
            actions.resetForm()
            document.getElementById('medias').files = null
            document.getElementById('medias').value = null
          }, 400);
        }}
        className={classes.elementMargin}
      >
        {({ isSubmitting }) => (
          <Form className={classes.formContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.token")}
                  name="token"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.mediaMessage.number")}
                  name="number"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.body")}
                  name="body"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12}  md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.userId")}
                  name="userId"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12}  md={6}>
                <Field
                  as={TextField}
                  label={i18n.t("messagesAPI.textMessage.queueId")}
                  name="queueId"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  className={classes.textField}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  type="file"
                  name="medias"
                  id="medias"
                  required
                  onChange={(e) => setFile(e.target.files)}
                  className={classes.fileInput}
                />
              </Grid>
              <Grid item xs={12} className={classes.textRight}>
                <Button
                  type="submit"
                  variant="contained"
                  className={classes.submitButton}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  ) : 'Enviar'}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    )
  }

  return (
    <MainContainer>
      <Paper className={classes.mainPaper} elevation={0}>
        <MainHeader>
          <Title>{i18n.t("messagesAPI.API.title")}</Title>
        </MainHeader>

        {/* Métodos */}
        <Paper className={classes.sectionPaper} elevation={0}>
          <Typography className={classes.sectionSubtitle}>
            {i18n.t("messagesAPI.API.methods.title")}
          </Typography>
          <Typography className={classes.sectionText} component="div">
            <ol>
              <li>{i18n.t("messagesAPI.API.methods.messagesText")}</li>
              <li>{i18n.t("messagesAPI.API.methods.messagesMidia")}</li>
            </ol>
          </Typography>
        </Paper>

        {/* Instruções */}
        <Paper className={classes.sectionPaper} elevation={0}>
          <Typography className={classes.sectionSubtitle}>
            {i18n.t("messagesAPI.API.instructions.title")}
          </Typography>
          <Typography className={classes.sectionText} component="div">
            <b>{i18n.t("messagesAPI.API.instructions.comments")}</b>
            <ul>
              <li>{i18n.t("messagesAPI.API.instructions.comments1")}</li>
              <li>
                {i18n.t("messagesAPI.API.instructions.comments2")}
                <ul>
                  <li>{i18n.t("messagesAPI.API.instructions.codeCountry")}</li>
                  <li>{i18n.t("messagesAPI.API.instructions.code")}</li>
                  <li>{i18n.t("messagesAPI.API.instructions.number")}</li>
                </ul>
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Mensagem de Texto */}
        <Paper className={classes.sectionPaper} elevation={0}>
          <Typography className={classes.sectionSubtitle}>
            {i18n.t("messagesAPI.API.text.title")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography className={classes.sectionText} component="div">
                <p>{i18n.t("messagesAPI.API.text.instructions")}</p>
                <p>
                  <b>Endpoint: </b> {getEndpoint()}
                </p>
                <p>
                  <b>Método: </b> POST
                </p>
                <p>
                  <b>Headers: </b> Authorization Bearer (token registrado) e Content-Type (application/json)
                </p>
                <p>
                  <b>Body: </b>
                </p>
                <pre style={{
                  fontFamily: '"Inter", "Poppins", sans-serif',
                  fontSize: 13,
                  padding: 12,
                  borderRadius: 8,
                  backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.02)" : "#F8FAFC",
                  border: theme.palette.type === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid #E2E8F0",
                  overflow: "auto",
                }}>
{`{
  "number": "558599999999",
  "body": "Message",
  "userId": "ID usuário",
  "queueId": "ID Fila",
  "sendSignature": "Assinar mensagem true/false"
}`}
                </pre>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={0} style={{
                borderRadius: 12,
                border: theme.palette.type === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid #E2E8F0",
              }}>
                <CardContent>
                  <Typography className={classes.sectionSubtitle} style={{ marginTop: 0 }}>
                    Teste de Envio
                  </Typography>
                  {renderFormMessageText()}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>

        {/* Mensagem de Mídia */}
        <Paper className={classes.sectionPaper} elevation={0}>
          <Typography className={classes.sectionSubtitle}>
            {i18n.t("messagesAPI.API.media.title")}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography className={classes.sectionText} component="div">
                <p>{i18n.t("messagesAPI.API.media.instructions")}</p>
                <p>
                  <b>Endpoint: </b> {getEndpoint()}
                </p>
                <p>
                  <b>Método: </b> POST
                </p>
                <p>
                  <b>Headers: </b> Authorization Bearer (token cadastrado) e Content-Type (multipart/form-data)
                </p>
                <p>
                  <b>FormData: </b>
                </p>
                <ul>
                  <li><b>number: </b> 558599999999</li>
                  <li><b>body:</b> Message</li>
                  <li><b>userId:</b> ID usuário</li>
                  <li><b>queueId:</b> ID da fila</li>
                  <li><b>medias: </b> arquivo</li>
                  <li><b>sendSignature:</b> Assinar mensagem true/false</li>
                </ul>
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={0} style={{
                borderRadius: 12,
                border: theme.palette.type === "dark" ? "1px solid rgba(255,255,255,0.06)" : "1px solid #E2E8F0",
              }}>
                <CardContent>
                  <Typography className={classes.sectionSubtitle} style={{ marginTop: 0 }}>
                    Teste de Envio
                  </Typography>
                  {renderFormMessageMedia()}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      </Paper>
    </MainContainer>
  );
};

export default MessagesAPI;